import * as Sentry from '@sentry/nextjs'
import { severityLevelFromString } from '@sentry/utils'
import sentryConfig from 'configs/sentry'
import { getCookie } from 'helpers/cookie'
import { generateShortUuid } from 'helpers/generate/uuid'
import isEmpty from 'helpers/is-empty'
import { getPageUrlWithoutQuery } from 'helpers/url'
const sendExceptionToSentry = (error: Error) => {
  Sentry.captureException(error)
}

function initLogger(
  user = {
    id: getCookie('user_id'),
    username: getCookie('username'),
    email: getCookie('email'),
  },
): void {
  if (sentryConfig.enabled === 'true') {
    if (!isEmpty(user.id)) {
      setLogUser(user.id, user.username, user.email)
    }
  }
}

/**
 * @param {Error} error
 * @return {Void}
 */
function sendError(error: Error): void {
  if (sentryConfig.enabled === 'true') {
    sendExceptionToSentry(error)
  }
}

function setLogUser(
  userId: string,
  userName?: string,
  email?: string,
): void {
  if (sentryConfig.enabled === 'true') {
    Sentry.setUser({ id: userId, username: userName, email: email })
  }
}

function resetLogUser(): void {
  if (sentryConfig.enabled === 'true') {
    Sentry.configureScope((scope) => scope.setUser(null))
  }
}

/**
 * Normalizes log inputs into a string message before forwarding to Sentry.
 *
 * Sentry's captureMessage treats non-string objects as synthetic exceptions
 * ("Object captured as exception with keys: ..."). Callers frequently pass
 * error objects (Axios rejections, Error instances, or plain error objects),
 * so this extracts the underlying message string.
 *
 * @param {unknown} message - Raw message string, Error instance, or error object
 * @return {string} Extracted message string
 */
function extractLogMessage(message: unknown): string {
  if (typeof message === 'string') {
    return message
  }
  if (!message) {
    return String(message)
  }
  if (typeof message === 'object') {
    const candidate = message as {
      message?: unknown
      error?: unknown
      response?: {
        status?: unknown
        data?:
          | {
              message?: unknown
              error?: unknown
            }
          | unknown
      }
    }
    const responseData = candidate.response?.data
    if (
      responseData &&
      typeof responseData === 'object' &&
      'message' in responseData &&
      typeof responseData.message === 'string' &&
      responseData.message
    ) {
      return responseData.message
    }
    if (
      responseData &&
      typeof responseData === 'object' &&
      'error' in responseData &&
      typeof responseData.error === 'string' &&
      responseData.error
    ) {
      return responseData.error
    }
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData.trim()
    }
    if (typeof candidate.message === 'string' && candidate.message) {
      return candidate.message
    }
    if (typeof candidate.error === 'string' && candidate.error) {
      return candidate.error
    }
    if (candidate.response?.status) {
      return `Request failed with status code ${String(candidate.response.status)}`
    }
    try {
      const json = JSON.stringify(message)
      if (json && json !== '{}') {
        return json
      }
    } catch {
      // Ignore circular reference or serialization errors
    }
  }
  return String(message)
}

/**
 * Sends an informational or custom-severity message to Sentry.
 *
 * Accepts strings, Error instances, or error objects. When an object is
 * provided, extractLogMessage extracts a string representation first to
 * avoid Sentry wrapping the payload as a synthetic exception.
 *
 * @param {unknown} message - Message string or error payload
 * @param {Sentry.SeverityLevel} [level] - Severity level (defaults to 'info')
 */
function sendLog(
  message: unknown,
  level: Sentry.SeverityLevel = severityLevelFromString('info'),
): void {
  if (sentryConfig.enabled === 'true') {
    const logMessage = extractLogMessage(message)
    Sentry.captureMessage(logMessage, level)
  }
}

function stripUrlQueryAndHash(value: string): string {
  const queryIndex = value.indexOf('?')
  const hashIndex = value.indexOf('#')
  const delimiterIndex =
    queryIndex === -1
      ? hashIndex
      : hashIndex === -1
        ? queryIndex
        : Math.min(queryIndex, hashIndex)

  return delimiterIndex === -1 ? value : value.slice(0, delimiterIndex)
}

/**
 * Sends a message to Sentry with tags scoped to that event alone.
 *
 * Tag values must come from an allowlist of non-PII strings. For this event
 * alone, request URLs, Referer/Referrer headers, and navigation/HTTP breadcrumb
 * URLs have their query strings and hashes removed.
 *
 * @param {string} message - Message string
 * @param {Record<string, string>} tags - Tags attached to this event only
 * @param {Sentry.SeverityLevel} [level] - Severity level (defaults to 'warning')
 */
function sendTaggedMessage(
  message: string,
  tags: Record<string, string>,
  level: Sentry.SeverityLevel = severityLevelFromString('warning'),
): void {
  if (sentryConfig.enabled === 'true') {
    // This scoped processor runs after HttpContext and strips invite tokens
    // from URLs without changing global Sentry behavior.
    Sentry.withScope((scope) => {
      scope.addEventProcessor((event) => {
        const request = event.request
        const headers = request?.headers
        const sanitizedHeaders = headers && { ...headers }
        if (sanitizedHeaders) {
          Object.keys(sanitizedHeaders).forEach((name) => {
            if (/^(?:referer|referrer)$/i.test(name)) {
              sanitizedHeaders[name] = stripUrlQueryAndHash(
                sanitizedHeaders[name],
              )
            }
          })
        }

        const breadcrumbs = event.breadcrumbs?.map((breadcrumb) => {
          const isNavigation = breadcrumb.category === 'navigation'
          const isHttp =
            breadcrumb.type === 'http' ||
            breadcrumb.category === 'http' ||
            breadcrumb.category === 'fetch' ||
            breadcrumb.category === 'xhr' ||
            breadcrumb.category?.startsWith('http.')
          if (!breadcrumb.data || (!isNavigation && !isHttp)) {
            return breadcrumb
          }

          const data = { ...breadcrumb.data }
          const urlFields = isNavigation
            ? ['from', 'to']
            : ['url', 'from', 'to']
          urlFields.forEach((field) => {
            if (typeof data[field] === 'string') {
              data[field] = stripUrlQueryAndHash(data[field])
            }
          })

          if (isHttp) {
            for (const field of ['request', 'response']) {
              const value = data[field]
              if (value && typeof value.url === 'string') {
                data[field] = {
                  ...value,
                  url: stripUrlQueryAndHash(value.url),
                }
              }
            }
          }

          return { ...breadcrumb, data }
        })

        return {
          ...event,
          request: {
            ...request,
            url: getPageUrlWithoutQuery(),
            ...(sanitizedHeaders ? { headers: sanitizedHeaders } : {}),
          },
          ...(breadcrumbs ? { breadcrumbs } : {}),
        }
      })
      Sentry.captureMessage(message, { level, tags })
    })
  }
}

function setTag(tagName: string, value?: string): void {
  if (sentryConfig.enabled === 'true') {
    Sentry.setTag(tagName, value)
  }
}

function addBreadcrumb(
  category: string,
  message: string,
  level: Sentry.SeverityLevel,
  data?: {
    [key: string]: any
  },
): void {
  if (sentryConfig.enabled === 'true') {
    Sentry.addBreadcrumb({
      category,
      message,
      level,
      data,
    })
  }
}

let cachedShortUuid: string | null = null

export function getCachedShortUuid(length: number): string {
  if (cachedShortUuid) {
    return cachedShortUuid
  }
  cachedShortUuid = generateShortUuid(length)
  return cachedShortUuid
}

function getRequestID(): string {
  const currentTransaction = Sentry.getCurrentHub()
    ?.getScope()
    ?.getTransaction()
  const baseTraceId = currentTransaction?.traceId || ''
  const shortUuid = generateShortUuid(8)
  return baseTraceId ? `${baseTraceId}-${shortUuid}` : shortUuid
}

export {
  initLogger,
  sendError,
  setLogUser,
  resetLogUser,
  sendLog,
  sendTaggedMessage,
  extractLogMessage,
  setTag,
  addBreadcrumb,
  getRequestID,
}
